import type {
  Equation,
  EvaluatedVector,
} from "@src/features/equation-editor/equation";
import type { CSSProperties } from "react";
import type { Translate } from "@src/i18n/translate";
import { VectorSymbol } from "./VectorSymbol";
import { formatDisplayNumber } from "../lib/display-number";
import { getTransformedVectors } from "@src/features/equation-editor/lib/transformed-vectors";
import { renderEntryColumnTemplate } from "../lib/equation-layout";
import { useMemo } from "react";
import { useResultStatus } from "../hooks/use-result-status";
import type { Dimension } from "@src/math/types";

export function TransformedVectorMatrix<D extends Dimension>({
  equation,
  t,
}: {
  equation: Equation<D>;
  t: Translate;
}) {
  const transformedVectors = useMemo(
    () =>
      getTransformedVectors(equation.evaluation.value).map((vector) =>
        vector.map(formatDisplayNumber),
      ),
    [equation.evaluation.value],
  );
  const transformedColumns = transformedVectors.map((coordinates, index) => {
    const source: EvaluatedVector<D> = equation.evaluation.value.vectors[index];
    return { coordinates, source };
  });
  const resultRevision = JSON.stringify(transformedVectors);
  const resultStatus = useResultStatus(
    equation.evaluation.kind,
    resultRevision,
    t,
  );
  const template = renderEntryColumnTemplate(transformedVectors);
  return (
    <section className="equation-right" aria-label={t("computedVectors")}>
      <div
        id="stale-result-status"
        className="visually-hidden"
        role="status"
        aria-live="polite"
      >
        {resultStatus}
      </div>
      <article
        className={`result-matrix-card result-matrix-card-${String(equation.dimension)}`}
        data-stale={equation.evaluation.kind !== "current" || undefined}
        aria-describedby={
          equation.evaluation.kind !== "current"
            ? "stale-result-status"
            : undefined
        }
        aria-label={t("transformedVectorMatrix")}
      >
        <div
          className="result-column-labels"
          style={{ gridTemplateColumns: template }}
        >
          {equation.evaluation.value.vectors.map((vector) => (
            <div
              key={vector.id}
              className="result-column-label"
              style={{ "--vector": vector.color } as CSSProperties}
            >
              <math
                className="vector-label"
                aria-label={t("transformedVector", {
                  label: vector.label,
                })}
              >
                <VectorSymbol label={vector.label} prime />
              </math>
              <span className="vector-color-label" aria-hidden="true" />
            </div>
          ))}
        </div>
        <div
          className={`result-expression result-expression-${String(equation.dimension)}`}
          style={{ gridTemplateColumns: template }}
          role="group"
          aria-label={t("transformedVectorColumns")}
        >
          {Array.from({ length: equation.dimension }, (_, component) =>
            transformedColumns.map(({ coordinates, source }, index) => (
              <span
                key={`${String(index)}-${String(component)}`}
                className="result-component"
                data-result-vector-index={index}
                data-result-component-index={component}
              >
                <span className="visually-hidden">
                  {t("transformedVectorComponent", {
                    label: source.label,
                    component: component + 1,
                  })}
                  {": "}
                </span>
                <span className="result-component-value">
                  {coordinates[component]}
                </span>
              </span>
            )),
          )}
        </div>
        <div className="card-balance-row" aria-hidden="true" />
      </article>
    </section>
  );
}
